import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { formatCurrency, getCurrencySymbol, getCurrencyCode, usdToTry } from '../utils/currency';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function WithdrawPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState('');
  const [iban, setIban] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [currency, setCurrency] = useState('TRY'); // TRY or USD (for VIP)
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchWithdrawals();
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
    } catch (err) {
      console.error('User fetch error:', err);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await axios.get(`${API}/withdrawals`, config);
      setWithdrawals(response.data.withdrawals || []);
    } catch (err) {
      console.error('Withdrawals fetch error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount <= 0) {
      showMessage('❌ Lütfen geçerli bir tutar girin');
      return;
    }

    // If VIP user and USD selected, convert to TRY for backend
    const isVip = user?.account_type === 'VIP';
    const isUSD = currency === 'USD';
    if (isVip && isUSD) {
      withdrawAmount = usdToTry(withdrawAmount);
    }

    const minAmount = isVip && isUSD ? 3 : 100; // $3 or 100 TL
    const minAmountDisplay = isVip && isUSD ? '$3' : '100 TL';
    
    if (parseFloat(amount) < minAmount) {
      showMessage(`❌ Minimum çekim tutarı ${minAmountDisplay}`);
      return;
    }

    if (withdrawAmount > user?.balance) {
      showMessage('❌ Yetersiz bakiye');
      return;
    }

    if (!iban || iban.trim() === '') {
      showMessage('❌ Lütfen IBAN girin');
      return;
    }

    if (!accountHolder) {
      showMessage('❌ Lütfen hesap sahibi adını girin');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      
      const response = await axios.post(`${API}/withdrawals`, {
        amount: withdrawAmount,
        iban: iban,
        account_holder: accountHolder
      }, config);

      // Calculate remaining balance
      const remainingBalance = user.balance - withdrawAmount;
      
      // Show detailed success message as object for better rendering
      showMessage({
        success: true,
        withdrawalId: response.data.withdrawal_id,
        accountHolder: accountHolder,
        iban: iban,
        amount: withdrawAmount,
        remainingBalance: remainingBalance
      });
      
      setAmount('');
      setIban('');
      setAccountHolder('');
      fetchWithdrawals();
      fetchUser();
    } catch (err) {
      showMessage('❌ Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg) => {
    if (typeof msg === 'object' && msg.success) {
      // Success with data
      setModalData(msg);
      setModalMessage('');
    } else {
      // Error or simple message
      setModalMessage(msg);
      setModalData(null);
    }
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'PENDING': 'bg-yellow-900 text-yellow-300',
      'APPROVED': 'bg-green-900 text-green-300',
      'REJECTED': 'bg-red-900 text-red-300'
    };
    const labels = {
      'PENDING': 'Beklemede',
      'APPROVED': 'Onaylandı',
      'REJECTED': 'Reddedildi'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">🏧 Para Çekme</h1>
          <p className="text-gray-400 text-sm sm:text-base">Hesabınızdan para çekin</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Withdrawal Form */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-bold mb-4">Çekim Talebi Oluştur</h2>
            
            <div className="bg-gray-750 border border-gray-700 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-1">
                Mevcut Bakiye {user?.account_type === 'VIP' && '💎'}
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-green-400">
                {formatCurrency(user?.balance || 0, user)}
              </div>
              {user?.account_type === 'VIP' && currency === 'USD' && (
                <div className="text-xs text-gray-500 mt-1">
                  ≈ ₺{(user?.balance || 0).toFixed(2)} TL
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Currency Selector (VIP Only) */}
              {user?.account_type === 'VIP' && (
                <div>
                  <label className="block text-sm font-medium mb-2">💱 Para Birimi</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrency('TRY')}
                      className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                        currency === 'TRY'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      🇹🇷 TL Hesabı
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrency('USD')}
                      className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                        currency === 'USD'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      🇺🇸 Dolar Hesabı
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tutar ({user?.account_type === 'VIP' && currency === 'USD' ? '$' : '₺'}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={user?.account_type === 'VIP' && currency === 'USD' ? "3" : "100"}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-lg"
                  placeholder={user?.account_type === 'VIP' && currency === 'USD' ? 'Minimum $3' : 'Minimum 100 TL'}
                  required
                />
                <div className="text-xs text-gray-400 mt-1">
                  Minimum çekim tutarı: {user?.account_type === 'VIP' && currency === 'USD' ? '$3' : '100 TL'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">IBAN *</label>
                <input
                  type="text"
                  value={iban}
                  onChange={(e) => setIban(e.target.value.toUpperCase())}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white font-mono"
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  maxLength="32"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Hesap Sahibi *</label>
                <input
                  type="text"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white"
                  placeholder="Adınız Soyadınız"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-bold text-lg transition-colors"
              >
                {loading ? 'Gönderiliyor...' : 'Çekim Talebi Oluştur'}
              </button>
            </form>

            <div className="mt-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-sm">
              <div className="font-semibold mb-2">📌 Önemli Bilgiler:</div>
              <ul className="space-y-1 text-gray-300">
                <li>• Çekim talepleri onay sonrası işleme alınır</li>
                <li>• Minimum çekim tutarı {user?.account_type === 'VIP' ? '$3 veya 100 TL' : '100 TL'}dir</li>
                <li>• IBAN hesap sahibi adı ile uyuşmalıdır</li>
                <li>• İşlem süresi genellikle 1-3 iş günüdür</li>
                {user?.account_type === 'VIP' && (
                  <li>• 💎 VIP üyeler dolar veya TL hesabına çekim yapabilir</li>
                )}
              </ul>
            </div>
          </div>

          {/* Bekleyen Çekim Talepleri */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-bold mb-4">⏳ Bekleyen Çekim Talepleri</h2>
            
            {withdrawals.filter(w => w.status === 'PENDING').length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-5xl mb-4">✓</div>
                <div>Bekleyen çekim talebiniz yok</div>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {withdrawals.filter(w => w.status === 'PENDING').map((withdrawal) => (
                  <div key={withdrawal.id} className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Takip Kodu</div>
                        <div className="text-sm font-mono text-yellow-400">{withdrawal.id}</div>
                      </div>
                      {getStatusBadge(withdrawal.status)}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Tutar:</span>
                        <span className="font-bold text-white">{formatCurrency(withdrawal.amount, user)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Hesap Sahibi:</span>
                        <span className="text-white">{withdrawal.account_holder}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">IBAN:</span>
                        <span className="font-mono text-white text-xs">{withdrawal.iban}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Tarih:</span>
                        <span className="text-white">{withdrawal.created_at ? new Date(withdrawal.created_at).toLocaleString('tr-TR') : 'N/A'}</span>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-yellow-700 text-xs text-yellow-300">
                      ⏳ Onay bekleniyor
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tüm Çekim Geçmişi */}
        {withdrawals.filter(w => w.status !== 'PENDING').length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl font-bold mb-4">📋 Çekim Geçmişi</h2>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {withdrawals.filter(w => w.status !== 'PENDING').map((withdrawal) => (
                <div key={withdrawal.id} className={`border rounded-lg p-4 ${
                  withdrawal.status === 'APPROVED' ? 'bg-green-900/20 border-green-600' : 'bg-red-900/20 border-red-600'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xl font-bold">{formatCurrency(withdrawal.amount, user)}</div>
                      <div className="text-xs text-gray-400">
                        {withdrawal.created_at ? new Date(withdrawal.created_at).toLocaleString('tr-TR') : 'N/A'}
                      </div>
                    </div>
                    {getStatusBadge(withdrawal.status)}
                  </div>
                  <div className="text-sm space-y-1 text-gray-300">
                    <div>{withdrawal.account_holder}</div>
                    <div className="font-mono text-xs">{withdrawal.iban}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-75 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 bg-gray-800 border-2 border-gray-600 rounded-xl p-6 z-50 max-w-md w-full shadow-2xl">
            {modalData ? (
              /* Success modal with withdrawal details */
              <div>
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">✅</div>
                  <h3 className="text-xl font-bold text-white">Çekim Talebiniz Oluşturuldu!</h3>
                </div>
                
                <div className="bg-gray-750 border border-gray-600 rounded-lg p-4 space-y-3 mb-6">
                  <div className="flex justify-between items-start py-2 border-b border-gray-600">
                    <span className="text-gray-400 text-sm">📋 Takip Kodu:</span>
                    <span className="text-white text-xs font-mono max-w-[200px] break-all text-right">{modalData.withdrawalId}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <span className="text-gray-400 text-sm">👤 Hesap Sahibi:</span>
                    <span className="text-white font-semibold">{modalData.accountHolder}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <span className="text-gray-400 text-sm">🏦 IBAN:</span>
                    <span className="text-white font-mono text-xs">{modalData.iban}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <span className="text-gray-400 text-sm">💰 Çekim Tutarı:</span>
                    <span className="text-green-400 font-bold text-lg">{formatCurrency(modalData.amount, user)}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-gray-400 text-sm">💳 Kalan Bakiye:</span>
                    <span className="text-blue-400 font-bold text-lg">₺{modalData.remainingBalance.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 mb-6 text-center">
                  <span className="text-yellow-300 text-sm">⏳ Talebiniz onay bekliyor</span>
                </div>
                
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-semibold w-full transition-colors"
                >
                  Tamam
                </button>
              </div>
            ) : (
              /* Error or simple message modal */
              <div className="text-center">
                <div className="text-4xl mb-4">
                  {modalMessage.includes('✅') ? '✅' : '❌'}
                </div>
                <p className="text-lg mb-6">{modalMessage.replace('✅', '').replace('❌', '')}</p>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg font-semibold w-full"
                >
                  Tamam
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default WithdrawPage;
